/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Read resolved values out of a dry-run capture, so a row can say what it will
 * actually send (R4a, #898).
 *
 * **This is not a second resolver.** Resolution happens server-side in the real
 * driver; this reads the answer back out of the request the driver built, keyed the
 * same way the rows are — a VAST parameter by name, a header by name, an ORTB leaf
 * by path. Nothing here interprets a macro, consults a context, or decides a value.
 * That distinction is the whole of CLAUDE.md's one-expander rule: a frontend copy of
 * `expandMacrosInString` would drift, whereas reading a built request cannot.
 *
 * **An absent key is the answer, not a lookup failure.** #880 made an unresolved
 * macro in an ORTB body omit its key rather than send a literal `{token}` — correct
 * on the wire, and it removed the only authoring signal a bad macro had. So a leaf
 * whose path is missing from the built body resolved to nothing and *will be
 * omitted*, which is precisely what the row needs to say. The preview restores the
 * signal #880 took away.
 */

import { ORTB_BODY_PATTERN_AT, ORTB_BODY_PATTERN_EACH, ortbBodyPatternIndex } from '@ortb-body-leaf';

export interface ResolvedRequest {
  /** VAST query parameters, by name. */
  params: Record<string, string>;
  /** Request headers, by name (auth values already masked by the driver). */
  headers: Record<string, string>;
  /** Parsed ORTB body, for path lookup. Absent on the VAST path. */
  body?: Record<string, unknown>;
}

interface CaptureLike {
  request?: {
    url?: string;
    headers?: Record<string, string>;
    body?: string;
  };
}

/**
 * Project a dry-run capture into the three lookups the editors need.
 *
 * Returns `null` when there is nothing to read — an unconfigured source, or a
 * request that never got built. Callers render that as "not yet", never as an error
 * on every row (R4e).
 */
export function readResolvedRequest(capture: CaptureLike | undefined | null): ResolvedRequest | null {
  const request = capture?.request;
  if (!request) return null;

  const params: Record<string, string> = {};
  if (request.url) {
    try {
      // A synthetic base keeps relative endpoints working, the same trick the VAST
      // driver uses when applying decorator params.
      const parsed = new URL(request.url, 'http://localhost');
      parsed.searchParams.forEach((value, key) => {
        params[key] = value;
      });
    } catch {
      // A malformed URL is not worth a warning on every row; it simply yields no
      // parameter resolutions.
    }
  }

  let body: Record<string, unknown> | undefined;
  if (request.body) {
    try {
      const parsed: unknown = JSON.parse(request.body);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        body = parsed as Record<string, unknown>;
      }
    } catch {
      body = undefined;
    }
  }

  return { params, headers: request.headers ?? {}, ...(body ? { body } : {}) };
}

/**
 * Resolved value at an ORTB leaf path (`['imp', 0, 'video', 'mimes']`).
 *
 * `undefined` means the key is **absent from the built request**, which is the
 * #880 semantics: the macro resolved to nothing and the field will be omitted. The
 * caller renders that as an omission rather than as an empty value, because on the
 * wire those are different things.
 */
export function resolvedValueAtPath(
  body: Record<string, unknown> | undefined,
  path: ReadonlyArray<string | number>,
): string | undefined {
  if (!body || path.length === 0) return undefined;

  let current: unknown = body;
  for (let i = 0; i < path.length; i++) {
    const segment = path[i];
    if (current === null || current === undefined) return undefined;

    if (Array.isArray(current)) {
      // **A pattern leaf reads the impression it lands on** (RS4c): `$at.<key>` the one its key
      // addresses, `$each` the first — every impression received it, and the first is the one a row
      // about *every impression* can show without claiming to be about any other.
      if (segment === ORTB_BODY_PATTERN_EACH) {
        current = current[0];
        continue;
      }
      if (segment === ORTB_BODY_PATTERN_AT) {
        const at = ortbBodyPatternIndex(String(path[++i]), current.length);
        if (at === null) return undefined;
        current = current[at];
        continue;
      }
      const index = typeof segment === 'number' ? segment : Number(segment);
      if (!Number.isInteger(index) || index < 0 || index >= current.length) return undefined;
      current = current[index];
      continue;
    }

    if (typeof current !== 'object') return undefined;
    const key = String(segment);
    if (!Object.prototype.hasOwnProperty.call(current, key)) return undefined;
    current = (current as Record<string, unknown>)[key];
  }

  return formatResolved(current);
}

/**
 * Render a resolved value for a single row.
 *
 * An explicit `null` is a value the operator authored and the exchange will receive,
 * so it reads as `null` rather than as an omission — the two are different on the
 * wire, and conflating them is the mistake #923 already fought over empty strings.
 */
function formatResolved(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/**
 * Resolution answers "what will this **become**", so it earns its place only where
 * the value is derived. A static row already shows its literal, and `→ <the same
 * literal>` is noise — on a body carrying twenty-five static `ext` fields that is
 * twenty-five rows of noise, which is how a useful mark stops being read. Each
 * editor therefore asks only for derived rows; there is no single predicate,
 * because "derived" is spelled differently on each surface (a leaf `kind`, a row
 * `mode`, a header carrying a mapping or a macro).
 */

/** What a row's resolution mark should show, given the current preview state. */
export interface RowResolution {
  value?: string;
  /**
   * Resolved to an **empty string** — distinct from `unresolved`, which is an
   * absent key.
   *
   * The two are different on the wire and the codebase has fought for the
   * distinction twice: #880 (an omitted key versus a literal token) and #923 (an
   * empty string is not a `0` and not a `false`, and for a regulatory field it is
   * the difference between "no signal" and an affirmative claim). Rendering an
   * empty resolution as a blank `→ ` told the operator nothing at all.
   */
  empty?: boolean;
  unresolved?: boolean;
  stale?: boolean;
}

/** Classify a looked-up value into the row's three reportable outcomes. */
function classify(value: string | undefined): RowResolution {
  if (value === undefined) return { unresolved: true };
  if (value === '') return { empty: true };
  return { value };
}

/**
 * Resolution for a VAST parameter row, by name.
 *
 * A parameter absent from the built URL resolved to nothing. On the VAST path an
 * unresolved optional parameter is omitted and a required one is sent empty, so
 * "absent" and "empty" both mean the exchange gets nothing usable — the row says
 * omitted either way, which is what the operator needs to act on.
 */
export function resolutionForParam(
  name: string,
  preview: { resolved: ResolvedRequest | null; stale: boolean },
): RowResolution | undefined {
  if (preview.stale) return { stale: true };
  if (!preview.resolved) return undefined;

  // A VAST parameter is omitted when optional and sent EMPTY when required, so the
  // two outcomes are both reachable here and mean different things to the exchange.
  return classify(preview.resolved.params[name.trim()]);
}

/** Resolution for a header row, by name. */
export function resolutionForHeader(
  name: string,
  preview: { resolved: ResolvedRequest | null; stale: boolean },
): RowResolution | undefined {
  if (preview.stale) return { stale: true };
  if (!preview.resolved) return undefined;

  return classify(preview.resolved.headers[name.trim()]);
}

/** Resolution for an ORTB body leaf, by path. */
export function resolutionForLeaf(
  path: ReadonlyArray<string | number>,
  preview: { resolved: ResolvedRequest | null; stale: boolean },
): RowResolution | undefined {
  if (preview.stale) return { stale: true };
  if (!preview.resolved) return undefined;

  return classify(resolvedValueAtPath(preview.resolved.body, path));
}
